import DayJS from 'dayjs'
import duration from 'dayjs/plugin/duration'
import es from 'dayjs/locale/es'
import i18n from '../i18n'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import pt from 'dayjs/locale/pt-br'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

DayJS.extend(isSameOrAfter)
DayJS.extend(isSameOrBefore)
DayJS.extend(isBetween)
DayJS.extend(duration)
DayJS.extend(timezone)
DayJS.extend(utc)
DayJS.extend(relativeTime)
DayJS.extend(localizedFormat)
DayJS.extend(isToday)
DayJS.locale(pt)
DayJS.locale(es)

// Get the correct locale according to the current i18n language
const supportedLocales = ['es', 'en', 'pt']

const getCurrentLocale = () => {
    const currentLang = i18n.language || i18n.resolvedLanguage || 'en'
    // Extract only the base language code (e.g., "pt-BR" -> "pt", "en-US" -> "en")
    const baseLang = currentLang.split('-')[0].toLowerCase()
    return supportedLocales.includes(baseLang) ? baseLang : 'en'
}

export const humanize = (date: DayJS.Dayjs) => {
    const now = DayJS().locale(getCurrentLocale())
    const diffSeconds = date.diff(now, 'second')
    const absSeconds = Math.abs(diffSeconds)

    // Minimum unit: minutes. Round seconds to minutes, with a minimum of 1 minute
    const minutes = Math.max(1, Math.round(absSeconds / 60))
    const target = diffSeconds >= 0 ? now.add(minutes, 'minute') : now.subtract(minutes, 'minute')

    // Use dayjs relative time formatting (respects locale)
    return now.to(target)
}

// Helper function to create dates with correct locale
export const createLocalizedDate = (date?: DayJS.ConfigType) => {
    return DayJS(date).locale(getCurrentLocale())
}

// Helper function for UTC dates with locale
export const localeUtcDate = (date: DayJS.Dayjs) => {
    return date.utc().locale(getCurrentLocale())
}

// Helper function to format dates with locale
export const formatWithLocale = (date: DayJS.ConfigType, format: string) => {
    return DayJS(date).locale(getCurrentLocale()).format(format)
}

// Helper function for relative dates with locale
export const relativeTimeWithLocale = (date: DayJS.ConfigType, baseDate?: DayJS.ConfigType) => {
    const locale = getCurrentLocale()
    const localizedDate = DayJS(date).locale(locale)

    if (baseDate) {
        const localizedBaseDate = DayJS(baseDate).locale(locale)
        return localizedDate.to(localizedBaseDate)
    }

    return localizedDate.fromNow()
}

// Helper function for relative time or day name + number
// <24h: returns relative time (e.g., "in 3 hours")
// >=24h: returns day name + day number (e.g., "Martes 15")
export const formatRelativeOrDay = (date: DayJS.ConfigType): string => {
    const locale = getCurrentLocale()
    const target = DayJS(date).locale(locale)
    const now = DayJS().locale(locale)
    const diffHours = target.diff(now, 'hour')

    if (diffHours < 24) {
        // Less than 24h: relative format
        return target.fromNow()
    }

    // 24h or more: day of week + day number
    // Format: "dddd D" → "martes 15"
    return target.format('dddd D')
}

export default DayJS
